import { useCallback, useEffect } from 'react';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface PurchaseLandingConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  subMessage?: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonClass?: string;
  isLoading?: boolean;
}

const PurchaseLandingConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  subMessage,
  confirmText = '확인',
  cancelText = '취소',
  confirmButtonClass = '!bg-blue-600 hover:!bg-blue-700',
  isLoading = false,
}: PurchaseLandingConfirmModalProps) => {
  // ESC key event handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isLoading, onClose]);

  const handleConfirm = useCallback(() => {
    if (isLoading) return;
    onConfirm();
  }, [isLoading, onConfirm]);

  const handleClose = useCallback(() => {
    if (isLoading) return;
    onClose();
  }, [isLoading, onClose]);

  if (!isOpen) return null;

  return (
    <div className="!fixed !inset-0 !z-50 !flex !items-center !justify-center !p-4 !bg-black/40">
      {/* 배경 클릭 시 닫기 */}
      <div className="!absolute !inset-0" onClick={handleClose} />

      {/* 모달 컨테이너 */}
      <div className="!relative !bg-white !rounded-lg !shadow-xl !max-w-md !w-full">
        {/* 헤더 */}
        <div className="!flex !items-center !justify-between !p-6 !border-b !border-gray-200">
          <h2 className="!text-xl !font-semibold !text-gray-900">{title}</h2>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="!p-1 !rounded-md hover:!bg-gray-100 !transition-colors !duration-200 disabled:!opacity-50"
          >
            <FontAwesomeIcon icon={faXmark} className="!w-5 !h-5 !text-gray-400" />
          </button>
        </div>

        {/* 컨텐츠 */}
        <div className="!p-6 !space-y-4">
          <p className="!text-center !text-gray-700 !whitespace-pre-line">{message}</p>
          {subMessage && (
            <p className="!text-left !text-sm !text-gray-500 !whitespace-pre-line">{subMessage}</p>
          )}
        </div>

        {/* 버튼 영역 */}
        <div className="!flex !gap-3 !p-6 !bg-gray-50 !rounded-b-lg">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="!flex-1 !px-4 !py-2.5 !bg-white !border !border-gray-300 !text-gray-700 !font-medium !rounded-md hover:!bg-gray-50 !transition-colors !duration-200 disabled:!opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`!flex-1 !px-4 !py-2.5 !text-white !font-medium !rounded-md !transition-colors !duration-200 disabled:!opacity-50 ${confirmButtonClass}`}
          >
            {isLoading ? (
              <span className="!flex !items-center !justify-center !gap-2">
                <span className="!animate-spin !rounded-full !h-4 !w-4 !border-2 !border-white !border-t-transparent"></span>
                처리 중...
              </span>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseLandingConfirmModal;